// Token budget counter (twin of desktop token_displays.py).
//
// The budget is CHAR-BASED, not LLM tokens: the server reports tokens_used for
// the block (each message costs max(1, floor(len/rate))), and remaining =
// budget - used. Colors follow the desktop thresholds: green > 50%, yellow >
// 25%, red below.
//
// Display types: numeric label, progress bar, fuel gauge (ring), coin
// containers, battery, and text_warnings (a toast-only mode with no persistent
// gauge). Any unknown type falls back to the numeric label.

import { useEffect, useId, useRef, useState } from "react";

type TokenSettings = Record<string, unknown>;

const GREEN = "#27ae60";
const YELLOW = "#f39c12";
const RED = "#e74c3c";

export function ratioColor(ratio: number): string {
  if (ratio > 0.5) return GREEN;
  if (ratio > 0.25) return YELLOW;
  return RED;
}

// Lighter variant used for the battery segment gradient.
export function ratioColorLight(ratio: number): string {
  if (ratio > 0.5) return "#2ecc71";
  if (ratio > 0.25) return "#f5b041";
  return "#ec7063";
}

const FONT = "Inter, 'Segoe UI', 'SF Pro Display', 'Helvetica Neue', sans-serif";

function NumericLabel({ remaining }: { remaining: number }) {
  return (
    <div
      className="jol-token-display jol-token-display--numeric"
      style={{
        padding: "8px 12px",
        margin: "6px 10px",
        background: "#f1f3f5",
        borderRadius: 8,
        fontWeight: 600,
        color: "#212529",
      }}
    >
      Tokens: {remaining}
    </div>
  );
}

function ProgressBar({
  remaining,
  safeBudget,
  ratio,
  settings,
}: {
  remaining: number;
  safeBudget: number;
  ratio: number;
  settings: TokenSettings;
}) {
  const showNumbers = settings.token_pb_show_numbers !== false; // default true
  const colorShift = settings.token_pb_color_shift !== false; // default true
  const fill = colorShift ? ratioColor(ratio) : GREEN;
  return (
    <div className="jol-token-display jol-token-display--bar" style={{ padding: "6px 10px" }}>
      <div
        style={{
          position: "relative",
          height: 22,
          borderRadius: 11,
          background: "#e9ecef",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${ratio * 100}%`,
            height: "100%",
            background: fill,
            transition: "width 0.3s ease, background 0.3s ease",
          }}
        />
        {showNumbers ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 600,
              color: "#212529",
            }}
          >
            {remaining} / {safeBudget}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function FuelGauge({
  remaining,
  ratio,
  settings,
}: {
  remaining: number;
  ratio: number;
  settings: TokenSettings;
}) {
  const showPct = settings.token_fg_show_percentage === true;
  const showNumbers = settings.token_fg_show_numbers !== false; // default true
  let text = "";
  if (showPct) text = `${Math.floor(ratio * 100)}%`;
  else if (showNumbers) text = String(remaining);
  // "no text in center" = both flags false -> text stays "".

  const R = 40;
  const circ = 2 * Math.PI * R;
  const dash = Math.max(0, ratio) * circ;
  const color = ratioColor(ratio);
  const fontSize = text.length <= 4 ? 18 : 14;

  return (
    <div className="jol-token-display jol-token-display--ring" style={{ padding: "6px 10px" }}>
      <svg width={100} height={100} viewBox="0 0 100 100">
        <circle cx={50} cy={50} r={R} fill="none" stroke="#e0e0e0" strokeWidth={12} strokeLinecap="round" />
        {ratio > 0 ? (
          <circle
            cx={50}
            cy={50}
            r={R}
            fill="none"
            stroke={color}
            strokeWidth={12}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circ}`}
            transform="rotate(-90 50 50)"
          />
        ) : null}
        {text ? (
          <text
            x={50}
            y={50}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={fontSize}
            fontWeight={700}
            fill="#2c3e50"
            fontFamily={FONT}
          >
            {text}
          </text>
        ) : null}
      </svg>
    </div>
  );
}

function Coins({
  remaining,
  budget,
  settings,
}: {
  remaining: number;
  budget: number;
  settings: TokenSettings;
}) {
  const idb = useId().replace(/:/g, "");
  const perCoin = Math.max(
    typeof settings.token_coins_per_coin === "number" ? settings.token_coins_per_coin : 100,
    1,
  );
  const numCoins = Math.min(Math.ceil(budget / perCoin), 20);
  if (numCoins <= 0) return null;

  const coinSize = 32;
  const spacing = 6;
  const pad = 6;
  const svgW = pad * 2 + numCoins * coinSize + (numCoins - 1) * spacing;
  const svgH = 44;
  const y = (svgH - coinSize) / 2;

  // Distribute the remaining tokens across coins, rightmost coin first (index
  // 0 = rightmost). The leftmost coins empty first, matching the desktop.
  let rem = remaining;
  const fills: number[] = [];
  for (let i = 0; i < numCoins; i++) {
    let f = 0;
    if (rem >= perCoin) {
      f = 1;
      rem -= perCoin;
    } else if (rem > 0) {
      f = rem / perCoin;
      rem = 0;
    }
    fills.push(f);
  }

  const coins = [];
  for (let p = 0; p < numCoins; p++) {
    const i = numCoins - 1 - p; // visual position p (left->right); i is fill index
    const fill = fills[i];
    const x = pad + p * (coinSize + spacing);
    const cx = x + coinSize / 2;
    const cy = y + coinSize / 2;
    const filled = fill > 0;
    const fillHeight = coinSize * fill;
    const fillY = y + coinSize - fillHeight;
    const inner = 4;
    coins.push(
      <g key={p}>
        <circle cx={cx} cy={cy} r={coinSize / 2} fill={`url(#${idb}-gray)`} stroke="#546e7a" strokeWidth={1.5} />
        {filled ? (
          <>
            <clipPath id={`${idb}-clip-${p}`}>
              <rect x={x} y={fillY} width={coinSize} height={fillHeight} />
            </clipPath>
            <circle
              cx={cx}
              cy={cy}
              r={coinSize / 2}
              fill={`url(#${idb}-gold)`}
              stroke="#e65100"
              strokeWidth={1.5}
              clipPath={`url(#${idb}-clip-${p})`}
            />
          </>
        ) : null}
        <circle
          cx={cx}
          cy={cy}
          r={coinSize / 2 - inner}
          fill="none"
          stroke={filled ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.06)"}
          strokeWidth={1}
        />
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={Math.round(coinSize * 0.45)}
          fontWeight={700}
          fill={filled ? "#8d6e00" : "#455a64"}
          fontFamily={FONT}
        >
          T
        </text>
      </g>,
    );
  }

  return (
    <div className="jol-token-display jol-token-display--coins" style={{ padding: "6px 10px" }}>
      <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
        <defs>
          <radialGradient id={`${idb}-gold`} cx="40%" cy="35%" r="70%">
            <stop offset="0%" stopColor="#fff176" />
            <stop offset="30%" stopColor="#ffd54f" />
            <stop offset="70%" stopColor="#ffb300" />
            <stop offset="100%" stopColor="#ff8f00" />
          </radialGradient>
          <radialGradient id={`${idb}-gray`} cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#9e9e9e" />
            <stop offset="80%" stopColor="#757575" />
            <stop offset="100%" stopColor="#616161" />
          </radialGradient>
        </defs>
        {coins}
      </svg>
    </div>
  );
}

function Battery({
  remaining,
  ratio,
  settings,
}: {
  remaining: number;
  ratio: number;
  settings: TokenSettings;
}) {
  const idb = useId().replace(/:/g, "");
  const numSegments =
    typeof settings.token_bat_segments === "number" && settings.token_bat_segments > 0
      ? settings.token_bat_segments
      : 4;
  const flashCritical = settings.token_bat_flash_critical !== false; // default true
  const showNumbers = settings.token_bat_show_numbers === true; // default false

  const filledSegments = ratio > 0 ? Math.ceil(ratio * numSegments) : 0;
  const critical = flashCritical && filledSegments <= 1 && remaining > 0;

  const [flashOn, setFlashOn] = useState(true);
  useEffect(() => {
    if (!critical) {
      setFlashOn(true);
      return;
    }
    const id = setInterval(() => setFlashOn((v) => !v), 500);
    return () => clearInterval(id);
  }, [critical]);

  const W = 110;
  const H = 44;
  const bodyX = 4;
  const bodyY = 6;
  const bodyW = W - 20;
  const bodyH = H - 12;
  const cornerR = 6;
  const tipW = 8;
  const tipH = Math.floor(bodyH / 3);
  const tipX = bodyX + bodyW;
  const tipY = bodyY + Math.floor((bodyH - tipH) / 2);

  const segMargin = 4;
  const segSpacing = 3;
  const segAreaW = bodyW - 2 * segMargin;
  const segW = (segAreaW - (numSegments - 1) * segSpacing) / numSegments;
  const segH = bodyH - 2 * segMargin;
  const segY = bodyY + segMargin;

  const base = ratioColor(ratio);
  const light = ratioColorLight(ratio);

  const segs = [];
  for (let i = 0; i < numSegments; i++) {
    const segX = bodyX + segMargin + i * (segW + segSpacing);
    const isFilled = i < filledSegments;
    let fill: string;
    if (isFilled) {
      if (filledSegments <= 1 && flashCritical && !flashOn) fill = "#eceff1";
      else fill = `url(#${idb}-seg)`;
    } else {
      fill = "#cfd8dc";
    }
    segs.push(<rect key={i} x={segX} y={segY} width={segW} height={segH} rx={3} ry={3} fill={fill} />);
  }

  return (
    <div className="jol-token-display jol-token-display--battery" style={{ padding: "6px 10px" }}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        <defs>
          <linearGradient id={`${idb}-seg`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={light} />
            <stop offset="100%" stopColor={base} />
          </linearGradient>
        </defs>
        <rect x={tipX} y={tipY} width={tipW} height={tipH} rx={2} ry={2} fill="#90a4ae" stroke="#78909c" strokeWidth={2} />
        <rect
          x={bodyX}
          y={bodyY}
          width={bodyW}
          height={bodyH}
          rx={cornerR}
          ry={cornerR}
          fill="#eceff1"
          stroke="#37474f"
          strokeWidth={2.5}
        />
        {segs}
        {showNumbers ? (
          <text
            x={bodyX + bodyW / 2}
            y={bodyY + bodyH / 2}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={Math.round(bodyH * 0.5)}
            fontWeight={700}
            fill="#263238"
            fontFamily={FONT}
          >
            {remaining}
          </text>
        ) : null}
      </svg>
    </div>
  );
}

type Warning = { threshold: number; message: string };

const DEFAULT_WARNINGS: Warning[] = [
  { threshold: 50, message: "⚠️ You have used half of your tokens." },
  { threshold: 25, message: "⚠️ Warning: 25% of tokens remaining." },
  { threshold: 10, message: "🚨 Critical: Only 10% of tokens remaining!" },
];

function parseWarnings(raw: unknown): Warning[] {
  if (!Array.isArray(raw)) return DEFAULT_WARNINGS;
  const out: Warning[] = [];
  for (const w of raw) {
    if (w && typeof w === "object") {
      const t = (w as { threshold?: unknown }).threshold;
      const m = (w as { message?: unknown }).message;
      if (typeof t === "number" && typeof m === "string") out.push({ threshold: t, message: m });
    }
  }
  return out.length ? out : DEFAULT_WARNINGS;
}

type ToastItem = { id: number; message: string; leaving: boolean };

// text_warnings mode: no persistent gauge. A floating pill appears once each
// time the remaining percentage drops to or below a configured threshold, holds
// for 3s, then fades out (twin of desktop TokenTextWarnings + TokenWarningToast).
function TextWarnings({
  used,
  budget,
  settings,
}: {
  used: number;
  budget: number;
  settings: TokenSettings;
}) {
  const safeBudget = Math.max(1, budget);
  const remaining = Math.max(0, safeBudget - used);
  const remainingPct = (remaining / safeBudget) * 100;
  const warnings = parseWarnings(settings.token_warnings);

  const firedRef = useRef<Set<number>>(new Set());
  const timersRef = useRef<number[]>([]);
  const nextId = useRef(0);
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => () => timersRef.current.forEach((t) => clearTimeout(t)), []);

  useEffect(() => {
    for (const w of warnings) {
      if (remainingPct <= w.threshold && !firedRef.current.has(w.threshold)) {
        firedRef.current.add(w.threshold);
        if (!w.message) continue;
        const id = nextId.current++;
        setToasts((ts) => [...ts, { id, message: w.message, leaving: false }]);
        const hold = window.setTimeout(
          () => setToasts((ts) => ts.map((t) => (t.id === id ? { ...t, leaving: true } : t))),
          3000,
        );
        const drop = window.setTimeout(() => setToasts((ts) => ts.filter((t) => t.id !== id)), 3800);
        timersRef.current.push(hold, drop);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingPct]);

  return (
    <div
      className="jol-token-warnings"
      style={{
        position: "absolute",
        top: 12,
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        zIndex: 50,
        pointerEvents: "none",
        width: "max-content",
        maxWidth: "calc(100% - 40px)",
      }}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          role="status"
          style={{
            background: "rgba(44, 62, 80, 0.86)",
            color: "#ffffff",
            fontSize: 15,
            fontWeight: 600,
            fontFamily: FONT,
            padding: "10px 24px",
            borderRadius: 20,
            textAlign: "center",
            pointerEvents: "none",
            opacity: t.leaving ? 0 : 1,
            transition: "opacity 800ms ease-out",
          }}
        >
          {t.message}
        </div>
      ))}
    </div>
  );
}

export default function TokenBudgetDisplay({
  used,
  budget,
  displayType,
  settings,
}: {
  used: number;
  budget: number;
  displayType: string;
  settings: TokenSettings;
}) {
  const safeBudget = Math.max(1, budget);
  const remaining = Math.max(0, safeBudget - used);
  const ratio = remaining / safeBudget;

  if (displayType === "progress_bar")
    return <ProgressBar remaining={remaining} safeBudget={safeBudget} ratio={ratio} settings={settings} />;
  if (displayType === "fuel_gauge")
    return <FuelGauge remaining={remaining} ratio={ratio} settings={settings} />;
  if (displayType === "coins")
    return <Coins remaining={remaining} budget={safeBudget} settings={settings} />;
  if (displayType === "battery")
    return <Battery remaining={remaining} ratio={ratio} settings={settings} />;
  if (displayType === "text_warnings")
    return <TextWarnings used={used} budget={safeBudget} settings={settings} />;

  // numeric_label (default) and any unknown type.
  return <NumericLabel remaining={remaining} />;
}
